import { useEffect, useState } from "react";
import AbileneHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Abilene?", a: "We offer Abilene-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes." },
  { q: "Do I need to visit an office in Abilene?", a: "No. Our service is remote: you complete forms online, visit a local acceptance facility (like an Abilene post office or Taylor County clerk) for in-person verification if needed, then ship documents to us via overnight courier." },
  { q: "Where is the nearest passport acceptance facility in Abilene?", a: "Abilene has acceptance facilities at the Abilene Main Post Office, North Abilene Station, and the Taylor County Clerk's office. We provide a curated list with address, phone, and photo availability." },
  { q: "Is there a passport agency near Abilene?", a: "The nearest passport agency is in Dallas, approximately 180 miles east of Abilene. There is no passport agency in the Abilene metro area. For urgent needs, our registered courier service is the fastest legally available channel — as quick as 24 hours, without the 3.5-hour drive." },
  { q: "How much does expedited passport service cost in Abilene?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee covering document review, overnight shipping, and expedited submission. We offer a best price guarantee and rates typically 30–100% lower than third-party resellers." },
  { q: "What documents do Abilene applicants need?", a: "For a new adult passport: proof of citizenship, government-issued photo ID, DS-11 form, and passport photo. For renewals: your most recent passport, one photo, and the DS-82 form. For Dyess AFB military families: standard requirements apply — military IDs are not a substitute for civilian passport applications." },
  { q: "Can Dyess AFB military families use our service?", a: "Yes. Active duty military and their families stationed at Dyess AFB are welcome to use our expedited courier service. Military IDs are used as supplemental identification, but civilian passports still require the standard DS-11 or DS-82 forms and acceptance facility visits for new applications." },
  { q: "How do I track my Abilene passport application?", a: "Once we submit your application, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov." },
  { q: "What are the main reasons Abilene residents need expedited passports?", a: "Dyess AFB deployment orders, West Texas agricultural trade missions, and Abilene Christian University study abroad programs are the most common reasons. Abilene's geographic isolation — 180 miles from Dallas — makes our courier service especially valuable because residents cannot easily access a passport agency." },
  { q: "Can I get a passport photo in Abilene?", a: "Yes. Several Abilene CVS and Walgreens locations offer passport photos. We recommend confirming State Department photo standard compliance before visiting." },
];

function AbileneStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) { setActive(id); break; }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" }, { id: "services", label: "Services" }, { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" }, { id: "destinations", label: "Destinations" }, { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" }, { id: "comparison", label: "Compare" }, { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" }, { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>
        ))}
      </div>
    </div>
  );
}

function AbileneProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Abilene Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for Abilene-area travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Abilene travelers departing within 3 days. Includes same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For Dyess AFB families and business travelers with travel in 1–2 weeks. Balanced speed and value." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Abilene residents planning ahead. Significantly faster than standard post office processing (10–13 weeks)." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AbileneServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Abilene travelers with imminent departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Abilene residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Abilene adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Abilene residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Dyess AFB and Abilene families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport issuance for frequent Abilene business and agricultural travelers." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors that delay Abilene applications." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Abilene Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for Abilene's community — from Dyess AFB military families to ranchers in Taylor County to ACU students headed abroad.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AbileneLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Abilene</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Abilene is the principal city of West Central Texas — a community of 125,000 residents anchored by Dyess Air Force Base, three major universities, a historic downtown, and an agricultural economy that spans Taylor and Jones Counties. The city sits at the intersection of I-20 and US-83, serving as the commercial hub for a region extending well beyond city limits.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Dyess Air Force Base is home to the 7th Bomb Wing — the only active duty B-1B Lancer wing in the Air Force. The base employs over 5,000 active duty personnel and their families, generating significant passport demand for PCS orders, international training exercises, and overseas deployments. Military families at Dyess frequently need to renew passports on short timelines — especially when PCS orders arrive requiring overseas travel for temporary duty assignments or NATO exercises.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Abilene Christian University, Hardin-Simmons University, and McMurry University collectively enroll thousands of students and send hundreds abroad each year through mission trips, study abroad programs, and academic exchange programs. ACU's globally-minded campus culture — with mission programs in South Africa, Latin America, and Central Asia — creates consistent first-time passport demand from students who have never traveled internationally.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The agricultural economy surrounding Abilene is substantial. Taylor County's cotton, cattle, and grain operations generate international travel for commodity trading conferences, agricultural equipment expos in Germany, and livestock shows in Australia. Ranching families in the Big Country who need to attend Angus cattle conferences or meet with Brazilian livestock buyers cannot simply drive to Dallas for a same-day passport appointment.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            Abilene is approximately 180 miles from Dallas — a 3-hour drive. The nearest passport agency is in Dallas. For Abilene residents with urgent travel needs, our courier service eliminates the need for that drive while delivering faster processing than the standard post office channel. We regularly help Abilene ranchers, military families, and university students navigate passport urgencies without leaving home.
          </p>
        </div>
      </div>
    </section>
  );
}

function AbileneTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Abilene Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">ABI — Abilene Regional Airport</h3>
                <p className="text-gray-400 text-xs">Regional hub with connections to Dallas-Fort Worth</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Abilene Regional Airport offers daily connections to Dallas-Fort Worth, from which Abilene residents connect to international destinations. The two-leg travel pattern — ABI to DFW, then DFW international — means passport issues are more likely to be discovered at the DFW connection than at home in Abilene. Our service ensures passports are current before you leave home.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Abilene travelers connecting through DFW for international travel still need a valid passport at their first point of departure from ABI. Don't wait until you reach Dallas to check.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-plane-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Dyess AFB Deployments &amp; Training</h3>
                <p className="text-gray-400 text-xs">7th Bomb Wing B-1B deployments, NATO exercises, overseas TDY</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Military families at Dyess AFB face passport challenges unique to the Air Force lifestyle — PCS orders that arrive with 30-day timelines, TDY assignments to NATO bases in Europe, and international exercises that require civilian travel documentation. We understand the military passport process and can handle Dyess AFB families' civilian passport applications with the speed and accuracy that deployment timelines demand.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Military IDs are not substitutes for civilian passports when traveling to most international destinations. Dyess AFB families should maintain current civilian passports for all family members.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">ACU, HSU &amp; McMurry Study Abroad</h3>
                <p className="text-gray-400 text-xs">Mission trips, semester exchange, international research programs</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Abilene Christian University's global mission programs send students to South Africa, India, Central Asia, and Latin America. Hardin-Simmons University has international research programs in archaeology, theology, and education. Many Abilene university students — especially first-generation college students — have never had a passport. The program acceptance-to-orientation pipeline is often 6–10 weeks, requiring expedited processing to meet start dates.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants in Abilene must visit an acceptance facility in person. We guide students through the nearest facility with the earliest available appointment so they don't miss their program orientation.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-seedling-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Big Country Agriculture &amp; Trade</h3>
                <p className="text-gray-400 text-xs">Cotton, cattle, grain — Taylor County's international commodity ties</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">The agricultural economy surrounding Abilene reaches across Taylor, Jones, Callahan, and Shackelford counties. Cotton and cattle producers in the Big Country travel to international commodity conferences, Brazilian livestock shows, and German agricultural equipment expos. These agricultural business trips are often booked with short notice, and an expired passport discovered the week of departure creates a logistical crisis for a ranching operation that cannot be postponed.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Agricultural trade missions to Brazil and Australia often require visa applications that depend on passport validity. Start the passport process before you begin any visa applications for these destinations.</p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-map-pin-2-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Abilene's Geographic Isolation — Why Our Courier Service Matters</h3>
            <p className="text-white/60 text-sm leading-relaxed">Abilene is 180 miles from Dallas and 150 miles from Lubbock. The nearest passport agency is the Dallas Federal Building — a 3.5-hour drive each way. For Abilene residents with urgent travel needs, our registered courier service eliminates that drive while delivering faster results than standard post office channels.</p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function AbileneDestinationsSection() {
  const destinations = [
    { name: "Brazil & Argentina", context: "Big Country cattle trade missions, Angus conferences, Hereford livestock shows in South America", urgency: "Agricultural trade conference schedules are fixed months in advance", icon: "ri-map-pin-2-line" },
    { name: "Germany & Europe", context: "AGRITECHNICA agricultural equipment expo, cotton and grain commodity conferences, NATO exercise TDY", urgency: "European trade shows and NATO exercises have firm, non-negotiable dates", icon: "ri-map-pin-2-line" },
    { name: "Mexico", context: "Family heritage trips, border crossings, vacation travel for Abilene families", urgency: "Spring break and summer bookings peak February through June", icon: "ri-map-pin-2-line" },
    { name: "South Africa & Africa", context: "ACU and HSU mission trips, global health programs, wildlife conservation travel", urgency: "Mission program start dates are fixed — applicants must have passports before orientation", icon: "ri-map-pin-2-line" },
    { name: "Caribbean & Mexico Cruises", context: "Galveston cruise departures, Cancun resort vacations, spring break family travel", urgency: "Cruise bookings and resort deals happen 3–6 weeks out with compressed passport timelines", icon: "ri-map-pin-2-line" },
    { name: "Central Asia & Middle East", context: "ACU international exchange programs, Dyess AFB overseas TDY, energy sector consulting", urgency: "Government and military TDY orders arrive with fixed dates and short notice", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "35%", label: "of Abilene expedited requests are military or government-related" },
    { stat: "4–8 Weeks", label: "typical lead time for Abilene university study abroad programs" },
    { stat: "180 Miles", label: "Abilene's distance from the nearest Dallas passport agency" },
    { stat: "48 Hours", label: "fastest turnaround we've accommodated for a Dyess AFB family deployment" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Abilene Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across West Central Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/lubbock" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Lubbock <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/midland-tx" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Midland <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/dallas" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Dallas <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AbileneTestimonialsSection() {
  const testimonials = [
    { name: "SSgt. Rivera", location: "Dyess AFB, Abilene", text: "Got PCS orders with 3 weeks' notice and my wife's passport was expired. Fast Passport Center processed it in 5 days. We made our overseas report date with time to spare.", rating: 5 },
    { name: "Dr. Thompson", location: "ACU, Abilene", text: "I had a speaking engagement at a conference in Cape Town in 10 days and my passport needed renewal. They handled it in 48 hours. Professional and thorough.", rating: 5 },
    { name: "Bob R.", location: "Abilene Rancher", text: "Had a cattle trade mission to Brazil in 8 days. Expired passport. They got it back in 48 hours. The whole ranch operation was waiting on that trip.", rating: 5 },
    { name: "Emily S.", location: "ACU Student", text: "Got selected for ACU's mission program to India and needed my first passport. They got it back in 7 days. I made my orientation flight by two days.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Abilene Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AbileneAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Abilene Main Post Office", address: "341 Pine St, Abilene, TX 79601", phone: "(325) 677-4521", photo: "Yes", appointment: "Required" },
    { name: "North Abilene Station", address: "4101 N 1st St, Abilene, TX 79603", phone: "(325) 695-3317", photo: "Yes", appointment: "Walk-in" },
    { name: "South Abilene Post Office", address: "3665 S 14th St, Abilene, TX 79605", phone: "(325) 698-5701", photo: "Yes", appointment: "Walk-in" },
    { name: "Taylor County Clerk", address: "300 Oak St, Abilene, TX 79602", phone: "(325) 674-1202", photo: "No", appointment: "Required" },
    { name: "Buffalo Gap Post Office", address: "200 S Center St, Buffalo Gap, TX 79508", phone: "(325) 572-3591", photo: "No", appointment: "Walk-in" },
    { name: "Sweetwater Post Office", address: "205 NE 4th Ave, Sweetwater, TX 79556", phone: "(325) 235-8413", photo: "Yes", appointment: "Required" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Abilene Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility. Here are Abilene-area locations with contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. Call ahead to confirm availability. We can help find the earliest available slot across all Abilene-area facilities.</p>
        </div>
      </div>
    </section>
  );
}

function AbileneComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Abilene Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Dallas Agency (180 miles)</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "Same day (emergencies only)" },
                { feature: "Appointment Required", us: "No", post: "Yes", reseller: "Yes, very limited" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "In-person only" },
                { feature: "Drive Required", us: "No", post: "Local only", reseller: "180-mile drive" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Agency fees + travel costs" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Agency walk-in" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "In-person at agency" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "N/A" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function AbileneMistakesSection() {
  const mistakes = [
    { title: "Assuming a 3.5-Hour Drive Is the Only Option", desc: "Many Abilene residents think the only way to get a fast passport is to drive to Dallas. Our courier service delivers faster results than the Dallas agency — without leaving Abilene." },
    { title: "Military ID Instead of Civilian Passport", desc: "Dyess AFB families sometimes assume their military IDs suffice for international travel. Civilian passports are still required for all family members traveling internationally as civilians." },
    { title: "Using DS-82 When DS-11 Is Required", desc: "Abilene applicants often attempt the simpler renewal form when their passport was issued at 15 or younger. This causes rejection and adds 1–2 weeks. We review before you submit." },
    { title: "Forgetting Parental Consent for Minors", desc: "Big Country families frequently overlook the DS-3053 consent form when one parent is unavailable for the in-person visit. This is an automatic rejection." },
    { title: "Not Accounting for 6-Month Rule", desc: "Abilene travelers booking to Brazil, Germany, or South Africa often do not know their passport must have 6 months remaining beyond the return date." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Abilene Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AbileneHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit an Abilene-area acceptance facility. We find the nearest one with the earliest appointment." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed package overnight to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit through our registered State Department courier channel. Full tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Delivered to your Abilene address via secure overnight shipping within your chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Abilene Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AbileneFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Abilene Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AbileneHelpfulResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Abilene Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "U.S. State Department", desc: "Official passport information, forms, and processing status updates.", href: "https://travel.state.gov/content/travel/en/passports.html", icon: "ri-government-line" },
            { title: "Travel Advisories", desc: "Current safety and entry requirements for every country.", href: "https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html", icon: "ri-earth-line" },
            { title: "Visa Waiver Program", desc: "Check if your destination participates in the ESTA visa waiver program.", href: "https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program", icon: "ri-visa-line" },
          ].map((r, i) => (
            <a key={i} href={r.href} target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function AbileneFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Abilene Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">Whether you are flying from ABI in 3 days or heading on a trade mission in 3 weeks, we will get your passport processed on time. No 180-mile drive to Dallas required.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function AbilenePage() {
  useEffect(() => {
    document.title = "Emergency Passport Abilene, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/abilene/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Abilene, TX — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Dyess AFB military families, West Texas ranchers, ACU students, and Big Country travelers. No 180-mile drive to Dallas required.");
    setMeta("geo.placename", "Abilene, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "32.4487;-99.7331");
    setMeta("ICBM", "32.4487, -99.7331");
    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <AbileneHeroSection />
      <AbileneStickyNav />
      <AbileneProcessingTimeSection />
      <AbileneServicesSection />
      <AbileneLocalAreaSection />
      <AbileneTravelTimingSection />
      <AbileneDestinationsSection />
      <AbileneTestimonialsSection />
      <AbileneAcceptanceFacilitiesSection />
      <AbileneComparisonSection />
      <AbileneMistakesSection />
      <AbileneHowItWorksSection />
      <AbileneFAQSection />
      <AbileneHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/abilene" state="TX" />
      <AbileneFinalCTA />
    </div>
  );
}